<template>
  <div :class="['min-w-0', compact ? 'w-full' : '']">
    <div v-if="copyableContact" class="flex gap-3 min-w-0 items-start">
      <button
        v-if="canLinkAvatar"
        type="button"
        v-tooltip.top="showProfileTooltip ? profileTooltip : undefined"
        class="crc-table-avatar crc-table-avatar--compact crc-table-avatar--interactive"
        :aria-label="profileTooltip"
        @click.stop="goToProfile"
      >
        <img
          v-if="showAvatar"
          :src="avatarSrc"
          alt=""
          class="crc-table-avatar__img"
          @error="broken = true"
        />
        <span v-else>{{ initials }}</span>
      </button>
      <div
        v-else
        class="crc-table-avatar crc-table-avatar--compact"
      >
        <img
          v-if="showAvatar"
          :src="avatarSrc"
          alt=""
          class="crc-table-avatar__img"
          @error="broken = true"
        />
        <span v-else>{{ initials }}</span>
      </div>

      <div class="min-w-0 flex-1 flex flex-col gap-2">
        <button
          v-if="canLinkName"
          type="button"
          v-tooltip.top="showProfileTooltip ? profileTooltip : undefined"
          class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate leading-snug text-left hover:text-primary transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded"
          :aria-label="profileTooltip"
          @click.stop="goToProfile"
        >
          {{ displayName }}
        </button>
        <div v-else class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate leading-snug">
          {{ displayName }}
        </div>

        <div v-if="hasMetaRow" class="flex flex-wrap items-center gap-1.5">
          <Tag
            v-if="showRoleTag && roleDisplay"
            :value="roleDisplay"
            :severity="roleSeverity"
            class="text-xs max-w-full truncate"
          />
          <Tag
            v-if="person?.department_name && showDepartmentTag"
            :value="person.department_name"
            :style="departmentTagStyle"
            class="text-xs max-w-full truncate"
          />
          <span
            v-if="gradeDisplay"
            class="inline-flex items-center px-2 py-0.5 rounded-md bg-surface-100 dark:bg-surface-800 text-xs font-medium text-surface-700 dark:text-surface-300 shrink-0"
          >
            <i class="pi pi-chart-line mr-1 text-[0.65rem]" />{{ gradeDisplay }}
          </span>
          <span
            v-if="positionDisplay"
            class="inline-flex items-center px-2 py-0.5 rounded-md bg-surface-100 dark:bg-surface-800 text-xs font-medium text-surface-700 dark:text-surface-300 max-w-full truncate"
          >
            <i class="pi pi-briefcase mr-1 text-[0.65rem] shrink-0" />
            <span class="truncate">{{ positionDisplay }}</span>
          </span>
        </div>

        <div v-if="hasContactInfo" class="flex flex-col gap-1 min-w-0">
          <button
            v-if="person?.email"
            type="button"
            class="flex items-center gap-1.5 w-full min-w-0 text-left text-xs text-surface-500 dark:text-surface-400 rounded py-0.5 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            title="Click to copy email"
            @click="copyContact(person.email, 'Email')"
          >
            <i class="pi pi-envelope shrink-0 text-[0.7rem]" />
            <span class="truncate min-w-0">{{ person.email }}</span>
          </button>
          <button
            v-if="person?.phone"
            type="button"
            class="flex items-center gap-1.5 w-full min-w-0 text-left text-xs text-surface-500 dark:text-surface-400 rounded py-0.5 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-700 dark:hover:text-surface-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 tabular-nums"
            title="Click to copy phone"
            @click="copyContact(person.phone, 'Phone')"
          >
            <i class="pi pi-phone shrink-0 text-[0.7rem]" />
            <span class="truncate min-w-0">{{ formatPhoneDisplay(person.phone) }}</span>
          </button>
        </div>
        <p v-else-if="!hasProfileMeta" class="text-xs text-surface-400 m-0">No contact information</p>
      </div>
    </div>

    <template v-else>
      <div :class="['flex items-center min-w-0', compact ? 'gap-2 w-full' : 'gap-3 py-1']">
        <button
          v-if="canLinkAvatar"
          type="button"
          v-tooltip.top="showProfileTooltip ? profileTooltip : undefined"
          :class="avatarShellClass"
          :aria-label="profileTooltip"
          @click.stop="goToProfile"
        >
          <img
            v-if="showAvatar"
            :src="avatarSrc"
            alt=""
            class="crc-table-avatar__img"
            @error="broken = true"
          />
          <span v-else>{{ initials }}</span>
        </button>
        <div v-else :class="avatarShellClass">
          <img
            v-if="showAvatar"
            :src="avatarSrc"
            alt=""
            class="crc-table-avatar__img"
            @error="broken = true"
          />
          <span v-else>{{ initials }}</span>
        </div>

        <div class="min-w-0 flex-1 flex flex-col gap-0.5">
          <button
            v-if="canLinkName"
            type="button"
            v-tooltip.top="showProfileTooltip ? profileTooltip : undefined"
            class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate text-left hover:text-primary transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded"
            :aria-label="profileTooltip"
            @click.stop="goToProfile"
          >
            {{ displayName }}
          </button>
          <div v-else class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate">
            {{ displayName }}
          </div>
          <div
            v-if="compact && (showDepartmentTag || showRoleTag) && hasMetaRow"
            class="flex flex-wrap items-center gap-1 mt-0.5 min-w-0"
          >
            <Tag
              v-if="showRoleTag && roleDisplay"
              :value="roleDisplay"
              :severity="roleSeverity"
              class="text-[0.65rem] max-w-full truncate"
            />
            <Tag
              v-if="person?.department_name && showDepartmentTag"
              :value="person.department_name"
              :style="departmentTagStyle"
              class="text-[0.65rem] max-w-full truncate"
            />
            <span
              v-if="gradeDisplay"
              class="inline-flex items-center px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-[0.65rem] font-medium text-surface-600 dark:text-surface-300 shrink-0"
            >
              {{ gradeDisplay }}
            </span>
            <span
              v-if="positionDisplay"
              class="text-[0.65rem] text-surface-500 dark:text-surface-400 truncate min-w-0"
            >
              {{ positionDisplay }}
            </span>
          </div>
          <div
            v-if="showEmail && person?.email"
            class="text-xs text-surface-500 dark:text-surface-400 truncate"
          >
            {{ person.email }}
          </div>
          <div
            v-if="!compact && person?.department_name && !showDepartmentTag"
            class="text-xs text-surface-500 dark:text-surface-400 truncate"
          >
            {{ person.department_name }}
          </div>
        </div>
        <Tag
          v-if="!compact && showRoleTag && roleDisplay"
          :value="roleDisplay"
          :severity="roleSeverity"
          class="text-xs shrink-0 max-w-[8rem] truncate self-start"
        />
        <Tag
          v-if="!compact && showDepartmentTag && person?.department_name"
          :value="person.department_name"
          :style="departmentTagStyle"
          class="text-xs shrink-0 max-w-[8rem] truncate self-start"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { resolveAvatarUrl } from '@/utils/resolveAvatarUrl';
import { copyToClipboard, formatPhoneDisplay } from '@/utils/common';
import { getDepartmentTagStyle, getRoleDisplayName, getRoleSeverity } from '@/utils/tagColors';
import { openEmployeeProfile } from '@/utils/openEmployeeProfile';

const profileTooltip = 'View employee profile';

const props = defineProps({
  person: { type: Object, default: null },
  profileUserId: { type: Number, default: null },
  linkToProfile: { type: Boolean, default: false },
  /** Keep a draft overlay open (create-report modal). */
  openInNewTab: { type: Boolean, default: false },
  /** When false, only avatar navigates to profile (e.g. table rows). */
  linkNameToProfile: { type: Boolean, default: true },
  showProfileTooltip: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  showDepartmentTag: { type: Boolean, default: true },
  showEmail: { type: Boolean, default: true },
  showRoleTag: { type: Boolean, default: false },
  copyableContact: { type: Boolean, default: false },
});

const router = useRouter();
const toast = useToast();
const broken = ref(false);

watch(
  () => props.person?.avatar_url,
  () => {
    broken.value = false;
  }
);

const profileId = computed(() => {
  const fromProp = props.profileUserId;
  if (fromProp != null && Number(fromProp) > 0) return Number(fromProp);
  const fromPerson = props.person?.id;
  if (fromPerson != null && Number(fromPerson) > 0) return Number(fromPerson);
  return null;
});

const canLinkAvatar = computed(() => props.linkToProfile && profileId.value != null);

const canLinkName = computed(
  () => props.linkToProfile && props.linkNameToProfile && profileId.value != null
);

const avatarShellClass = computed(() => [
  'crc-table-avatar crc-table-avatar--compact',
  canLinkAvatar.value ? 'crc-table-avatar--interactive' : '',
]);

const displayName = computed(() => props.person?.name || props.person?.label || 'Employee');

const initials = computed(() => {
  const val = String(displayName.value || '').trim();
  if (!val) return 'NA';
  return val.split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
});

const avatarSrc = computed(() => {
  if (!props.person?.avatar_url) return '';
  return resolveAvatarUrl(props.person.avatar_url);
});

const showAvatar = computed(() => Boolean(avatarSrc.value) && !broken.value);

const departmentTagStyle = computed(() =>
  getDepartmentTagStyle(
    props.person?.department_id,
    props.person?.department_name,
    props.person?.department_tag_color ?? null,
    props.person?.department_tag_text_color ?? null
  )
);

const hasContactInfo = computed(() => Boolean(props.person?.email || props.person?.phone));

const positionDisplay = computed(() => String(props.person?.position_name ?? '').trim());

const gradeDisplay = computed(() => {
  const name = String(props.person?.grade_name ?? '').trim();
  if (name) return name;
  if (props.person?.grade_level != null && props.person.grade_level !== '') {
    return `Grade ${props.person.grade_level}`;
  }
  return '';
});

const roleCode = computed(() => String(props.person?.role_code ?? '').trim().toLowerCase());

const roleDisplay = computed(() => {
  const code = roleCode.value;
  if (code) return getRoleDisplayName(code);
  const name = String(props.person?.role_name ?? '').trim();
  return name;
});

const roleSeverity = computed(() => (
  roleCode.value ? getRoleSeverity(roleCode.value) : 'secondary'
));

const hasMetaRow = computed(
  () => Boolean(
    (props.showRoleTag && roleDisplay.value)
      || (props.showDepartmentTag && props.person?.department_name)
      || gradeDisplay.value
      || positionDisplay.value
  )
);

const hasProfileMeta = computed(
  () => Boolean(hasMetaRow.value || hasContactInfo.value)
);

const goToProfile = (event) => {
  openEmployeeProfile(router, profileId.value, {}, {
    newTab: props.openInNewTab,
    event,
  });
};

const copyContact = async (value, kind) => {
  const text = String(value || '').trim();
  if (!text) return;
  const result = await copyToClipboard(text, kind);
  toast.add({
    severity: result.success ? 'info' : 'warn',
    summary: result.success ? 'Copied' : 'Copy failed',
    detail: result.message,
    life: 2000,
  });
};
</script>
